import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Select } from 'antd';
import { AxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BsPhone } from 'react-icons/bs';
import { Link, useLocation } from 'react-router-dom';
import axios from '../../config/axios.ts';
import { AuthContext } from '../../context/auth.context.tsx';
import AuthLayout from '../../layouts/auth.layout.tsx';

type FieldType = {
	phone: string;
	password: string;
	remember: string;
	countryCode: string;
};

const VendorLogin = () => {
	const { setUser, setIsAuthenticated, setRole } = useContext(AuthContext);
	const [alertMessage, setAlertMessage] = useState<string | null>(null);
	const location = useLocation();
	const [countryCode, setCountryCode] = useState<string>('256');

	// login with phone mutation
	const { mutate: loginWithPhone, isPending: loginWithPhonePending } = useMutation({
		mutationFn: async (values: FieldType) => {
			const phoneWithCode = `${countryCode}${values.phone}`;
			const { data } = await axios.post('/users/login', {
				phone_number: phoneWithCode,
				password: values.password,
			});
			return data;
		},
		onSuccess: (data) => {
			window.localStorage.setItem('device_id', data.results.access_token);
			toast.success('Login successful');
			setUser(data.results.user);
			setRole(data.results.user.role);
			setIsAuthenticated(true);
			window.location.href = '/retailer/dashboard';
		},
		onError: (error: AxiosError<{ msg: string }>) => {
			if (error.response) {
				setAlertMessage('Invalid Credentials!');
			}
		},
	});

	// hide the error message after 3 seconds
	useEffect(() => {
		const timer = setTimeout(() => {
			setAlertMessage(null);
		}, 3000);
		return () => clearTimeout(timer);
	}, [alertMessage]);

	// get message from navigate state
	const state_message = location.state && location.state.message;

	const selectBefore = (
		<Select
			defaultValue={'+256'}
			prefix={<BsPhone />}
			onChange={(value) => setCountryCode(value)}
		>
			<Select.Option value="256">+256</Select.Option>
			<Select.Option value="88">+88</Select.Option>
		</Select>
	);

	return (
		<AuthLayout>
			<div className="flex h-full items-center justify-center">
				<div className="flex w-full max-w-[450px] flex-col items-center justify-center">
					<Link to={'/'}>
						<img
							src="/images/logo.png"
							alt="logo"
							className="h-14"
						/>
					</Link>

					<div className="pt-10" />

					<h1 className="text-center text-4xl font-bold text-black-7 dark:text-white">Welcome Back</h1>

					<div className="pt-4" />

					<p className="text-base font-medium text-gray-400 dark:text-gray-200">Just pop your phone number and password in here </p>

					<div className="pt-6" />

					{alertMessage ? (
						<Alert
							message={alertMessage}
							type={alertMessage.includes('successful') ? 'success' : 'error'}
							showIcon
							className="w-full"
						/>
					) : state_message ? (
						<Alert
							message={state_message}
							type={state_message.includes('successful') ? 'success' : 'error'}
							showIcon
							className="w-full"
						/>
					) : null}

					<Form
						initialValues={{ remember: true }}
						onFinish={(values) => {
							loginWithPhone(values);
						}}
						layout="vertical"
						className="w-full pt-2"
						requiredMark={false}
					>
						<Form.Item<FieldType>
							label={<div className="pl-1 text-base font-medium text-black-7 dark:text-white">Phone Number</div>}
							name="phone"
						>
							<Input
								addonBefore={selectBefore}
								size="large"
								placeholder="Enter your phone number"
								required
								type="tel"
								className="h-11"
								onKeyDown={(e) => {
									// If the key is not a number (0-9), prevent input
									if (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
										e.preventDefault();
									}
								}}
								onChange={(e) => {
									// Remove any non-numeric characters
									e.target.value = e.target.value.replace(/\D/g, '');
								}}
							/>
						</Form.Item>

						<Form.Item<FieldType>
							label={<div className="pl-1 text-base font-medium text-black-7 dark:text-white">Password</div>}
							name="password"
						>
							<Input.Password
								size="large"
								placeholder="Enter your password"
								required
								type="password"
								className="h-11"
							/>
						</Form.Item>

						<div className="flex items-center justify-between pb-3.5">
							<div className="mb-2">
								<Link to="/login">
									<span className="text-base font-medium text-[#6E5DED]">Login as admin</span>
								</Link>
							</div>
							<Link to={'/forgot-password'}>
								<span className="text-base font-medium text-[#6E5DED]">Forgot password?</span>
							</Link>
						</div>

						<Form.Item className="mb-2">
							<Button
								htmlType="submit"
								block
								size="large"
								shape="round"
								className="h-11 font-bold"
								type="primary"
								loading={loginWithPhonePending}
								disabled={loginWithPhonePending}
							>
								Login
							</Button>
						</Form.Item>
					</Form>

					<div className="pt-3" />
				</div>
			</div>
		</AuthLayout>
	);
};

export default VendorLogin;
